import { APIFactory } from 'ui-toolkit/rest/APIFactory';
import { axiosErrorHandler } from 'ui-toolkit/rest/axiosErrorHandler';
import {
  convertFromVendorTaxCertificationInformationRequestToVendorTaxCertificationInformationData,
  IVendorTaxCertificationInformationData,
  IVendorTaxCertificationInformationRequest,
} from '../../types/vendorDashboard/IVendorTaxCertificationInformation';
import { eTaxCertificationRequestStatus } from '../../types/order/title/eTaxCertificationRequestStatus';
import qs from 'qs';
import {
  convertFromVendorOrdersTaxReqResponseToSearchOrdersSettingsData,
  ISearchOrderTaxCertReqSettingsData,
} from '../../types/vendorDashboard/ISearchOrderTaxCertReqSettingsData';
import {
  convertFromVendorTaxCertificationsResponseToVendorTaxCertificationsData,
  IVendorTaxCertificationsData,
  IVendorTaxCertificationsPagedResponse,
} from '../../types/vendorDashboard/IVendorTaxCertifications';
import {
  convertFromVendorOrdersResponseToVendorOrdersData,
  IVendorOrdersData,
  IVendorOrdersResponse,
} from '../../types/vendorDashboard/IVendorOrders';
import {
  convertFromVendorOrdersResponseToSearchOrdersSettingsData,
  ISearchOrdersSettingsData,
} from '../../types/vendorDashboard/ISearchOrdersSettingsData';
import {
  convertFromVendorPayoffsResponseToVendorPayoffsData,
  IVendorPayoffsData,
  IVendorPayoffsResponse,
} from '../../types/vendorDashboard/IVendorPayoffs';
import {
  convertFromVendorPayoffInformationRequestToVendorPayoffInformationData,
  IVendorPayoffInformationData,
  IVendorPayoffInformationRequest,
} from '../../types/vendorDashboard/IVendorPayoffInformation';
import { eVendorServiceType } from '../../types/order/title/eVendorServiceType';
import { eTitleSearchStatus } from '../../types/order/title/eTitleSearchStatus';
import {
  convertFromVendorOrderInformationRequestToVendorOrderInformationData,
  IVendorOrderInformationData,
  IVendorOrderInformationRequest,
} from '../../types/vendorDashboard/IVendorOrderInformation';
import { IUploadDocument } from '../../types/order/documents/upload/interfaces';
import {
  convertFromCommentArrayResponseToCommentDataArray,
  convertFromCommentResponseToCommentData,
  IVendorCommentData,
} from 'types/order/comment/IVendorComment';
import {
  IStandardDocumentVersionData,
  IUpdatedDocumentResponse,
  convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionArray,
} from '../../types/order/documents/shared/interfaces';
import { baseUrlService } from '../../ui-toolkit/rest/BaseUrlService';
import { eCommentType } from '../../types/order/comment/eCommentType';
import { ICommentResponse } from '../../types/order/comment/IComment';

export const getOrdersTitleSearchAsync = async (
  statuses: eTitleSearchStatus[],
  pageSize: number,
  page: number,
  fileNumber?: string
): Promise<{ orders: IVendorOrdersData; setting: ISearchOrdersSettingsData }> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<IVendorOrdersResponse>(`/lender/v1/vendor/orders/title_search_requests`, {
      statuses,
      page,
      pageSize,
      fileNumber,
    })
    .then((response) => {
      return {
        orders: convertFromVendorOrdersResponseToVendorOrdersData(response.data),
        setting: convertFromVendorOrdersResponseToSearchOrdersSettingsData(response.data),
      };
    })
    .catch(axiosErrorHandler);

export const getTaxCertificationsAsync = async ({
  statuses,
  pageSize,
  pageNumber,
  fileNumber,
}: IGetTaxCertificationsAsyncProps): Promise<{
  taxCerts: IVendorTaxCertificationsData;
  setting: ISearchOrderTaxCertReqSettingsData;
}> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IVendorTaxCertificationsPagedResponse>(`/lender/v1/vendor/tax_certification_requests`, {
      params: {
        statuses,
        pageSize,
        pageNumber,
        fileNumber,
      },
      paramsSerializer: (params) => {
        // Might not be repeat because of timber issues. As in it isn't calling timber.
        return qs.stringify(params, { arrayFormat: 'comma', encode: false });
      },
    })
    .then((response) => {
      return {
        taxCerts: convertFromVendorTaxCertificationsResponseToVendorTaxCertificationsData(response.data),
        setting: convertFromVendorOrdersTaxReqResponseToSearchOrdersSettingsData(response.data),
      };
    })
    .catch(axiosErrorHandler);

export const getPayoffsAsync = async (
  statuses: eTitleSearchStatus[],
  pageSize: number,
  page: number,
  fileNumber?: string
): Promise<{ payoffRequests: IVendorPayoffsData; setting: ISearchOrdersSettingsData }> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<IVendorPayoffsResponse>(`/lender/v1/vendor/orders/payoff_requests`, {
      statuses,
      page,
      pageSize,
      fileNumber,
    })
    .then((response) => {
      return {
        payoffRequests: convertFromVendorPayoffsResponseToVendorPayoffsData(response.data),
        setting: convertFromVendorOrdersResponseToSearchOrdersSettingsData(response.data),
      };
    })
    .catch(axiosErrorHandler);

export const getOrderAsync = async (orderId: string): Promise<IVendorOrderInformationData> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IVendorOrderInformationRequest>(`/timber/api/internal/vendor_company_orders/${orderId}`)
    .then((response) => convertFromVendorOrderInformationRequestToVendorOrderInformationData(response.data))
    .catch(axiosErrorHandler);

export const getPayoffOrderAsync = async (orderId: string): Promise<IVendorPayoffInformationData> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IVendorPayoffInformationRequest>(`/lender/v1/vendor/orders/${orderId}/payoff_requests`)
    .then((response) => convertFromVendorPayoffInformationRequestToVendorPayoffInformationData(response.data))
    .catch(axiosErrorHandler);

export const getDocumentsAsync = async (orderId: string): Promise<IStandardDocumentVersionData[]> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IUpdatedDocumentResponse[]>(`/timber/api/internal/orders/${orderId}/documents/all`)
    .then((response) => convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionArray(response.data))
    .catch(axiosErrorHandler);

export const getCommentsAsync = async (orderId: string, types: eVendorServiceType[]): Promise<IVendorCommentData[]> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<ICommentResponse[]>(
      `/timber/api/internal/orders/${orderId}/comments?scope=vendor${types
        .map((type) => `&comment-type=${type}`)
        .join()}`
    )
    .then((response) => convertFromCommentArrayResponseToCommentDataArray(response.data))
    .catch(axiosErrorHandler);

export const deleteDocumentAsync = async (orderId: string, documentId: string): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation).delete<{}>(`/documents/v5/${documentId}`).catch(axiosErrorHandler);

export const sendTitleSearchToSpruceAsync = async (titleSearchRequestId: string): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .patch<{}>(`/lender/v1/vendor/title_search_requests/${titleSearchRequestId}/send_to_spruce`)
    .catch(axiosErrorHandler);

export const sendPayoffToSpruceAsync = async (payoffRequestId: string): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .patch<{}>(`/lender/v1/vendor/payoff_requests/${payoffRequestId}/send_to_spruce`)
    .catch(axiosErrorHandler);

export const updatePayoffETAAsync = async (payoffRequestId: string, eta?: string | null): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .patch<IVendorPayoffInformationRequest>(`/lender/v1/vendor/payoff_requests/${payoffRequestId}/eta`, { eta })
    .then((response) => convertFromVendorPayoffInformationRequestToVendorPayoffInformationData(response.data))
    .catch(axiosErrorHandler);

export const uploadDocumentAsync = async (orderId: string, document: IUploadDocument): Promise<string> => {
  const formData = new FormData();
  formData.append('file', document.file);
  formData.append('file_name', document.file.name);
  if (document.metadata) {
    formData.append(
      'json',
      JSON.stringify({
        ...document.metadata,
        original_file_name: document.file.name,
      })
    );
  }
  return APIFactory(baseUrlService.ApiLocation)
    .post<string>(
      `/documents/v4/${orderId}/${document.category}/${!document.isRepeatable ? 'versioned' : 'unversioned'}/upload`,
      formData
    )
    .then((response) => response.data)
    .catch(axiosErrorHandler);
};

export const postCommentAsync = async (
  orderId: string,
  commentContent: string,
  type: eCommentType
): Promise<IVendorCommentData> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<ICommentResponse>(`timber/api/internal/orders/${orderId}/comments`, {
      content: commentContent,
      type,
    })
    .then((response) => convertFromCommentResponseToCommentData(response.data))
    .catch(axiosErrorHandler);

export const getTaxCertificationRequestsAsync = async (
  orderId: string
): Promise<IVendorTaxCertificationInformationData> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IVendorTaxCertificationInformationRequest>(`timber/api/internal/orders/${orderId}/tax_certifications_requests`)
    .then((response) =>
      convertFromVendorTaxCertificationInformationRequestToVendorTaxCertificationInformationData(response.data)
    )
    .catch(axiosErrorHandler);

export const postSendTaxCertificationToSpruceAsync = async (taxCertRequestId: string): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<{}>(`timber/api/internal/tax_certifications_requests/${taxCertRequestId}/send_to_spruce`)
    .catch(axiosErrorHandler);

export const patchTaxCertificationETAAsync = async ({
  taxCertRequestId,
  eta,
}: {
  taxCertRequestId: string;
  eta?: string | null;
}): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .patch<IVendorTaxCertificationInformationRequest>(
      `/lender/v1/vendor/tax_certification_requests/${taxCertRequestId}/eta`,
      { eta }
    )
    .then((response) =>
      convertFromVendorTaxCertificationInformationRequestToVendorTaxCertificationInformationData(response.data)
    )
    .catch(axiosErrorHandler);

export interface IGetTaxCertificationsAsyncProps {
  statuses: eTaxCertificationRequestStatus[];
  pageSize: number;
  pageNumber: number;
  fileNumber?: string;
}
